export function vocalFaceReviewed(scene: any): boolean {
  const review = scene.lipsync_visual_review;
  return (
    !!scene.lipsync_source_asset_id &&
    !scene.motion_stale &&
    review?.status === "approved" &&
    review.source_motion_asset_id === scene.lipsync_source_asset_id
  );
}

export function vocalShotState(scene: any): {
  key: string;
  label: string;
  tone: string;
} {
  const v = scene.vocal_direction;
  if (!v?.valid)
    return {
      key: "selection",
      label: "Selection needs review",
      tone: "attention",
    };
  if (!v.selected)
    return { key: "unselected", label: "No visible singing", tone: "neutral" };
  // The Singing moments card makes this shot; Sync Labs never touches it.
  if (scene.singing_route === "avatar")
    return { key: "avatar", label: "Sings with Kling Avatar", tone: "neutral" };
  if (scene.lipsync_control?.mode === "off")
    return { key: "off", label: "Skipped for lip-sync", tone: "neutral" };
  if (!v.audio_confirmed)
    return {
      key: "audio",
      label: "Confirm the singing first",
      tone: "attention",
    };
  if (!scene.lipsync_source_asset_id || scene.motion_stale)
    return {
      key: "animation",
      label: "Waiting for animation",
      tone: "neutral",
    };
  if (scene.lipsync_ready && !scene.lipsync_stale)
    return { key: "complete", label: "Lip-sync complete", tone: "complete" };
  if (!vocalFaceReviewed(scene))
    return { key: "face", label: "Check animated face", tone: "attention" };
  if (scene.lipsync_candidate && !scene.lipsync_ready)
    return { key: "ready", label: "Ready for lip-sync", tone: "ready" };
  return {
    key: "blocked",
    label: scene.lipsync_reason || "Lip-sync needs review",
    tone: "attention",
  };
}

export function vocalBatchTargets(project: any): any[] {
  if (project.vocal_direction?.valid === false) return [];
  return (project.scenes || []).filter(
    (s: any) => vocalShotState(s).key === "ready",
  );
}

// The frame review must refer to the source clip, never an older lip-sync output.
export function vocalPreviewAsset(project: any, scene: any): any {
  const id =
    vocalShotState(scene).key === "complete"
      ? scene.motion_asset_id
      : scene.lipsync_source_asset_id;
  return (project.assets || []).find((asset: any) => asset.id === id);
}

export function vocalPanelStatus(project: any): {
  label: string;
  tone: string;
} {
  const chosen = (project.scenes || []).filter(
    (s: any) => s.vocal_direction?.selected,
  );
  const selected = chosen.filter((s: any) => s.singing_route !== "avatar");
  const avatar = chosen.length - selected.length;
  const active = (project.jobs || []).find(
    (job: any) =>
      ["generate_lipsync", "generate_lipsync_batch"].includes(job.kind) &&
      ["queued", "running", "retrying", "cancel_requested"].includes(
        job.status,
      ),
  );
  if (active)
    return {
      label:
        active.status === "cancel_requested"
          ? "Stopping safely"
          : active.status === "queued"
            ? "Lip-sync queued"
            : active.status === "retrying"
              ? "Lip-sync retrying"
              : "Lip-sync running",
      tone: "running",
    };
  if (
    project.vocal_direction?.valid === false ||
    selected.some((s: any) => !s.vocal_direction.valid)
  )
    return { label: "Selection needs review", tone: "attention" };
  if (!selected.length && avatar)
    return {
      label: `${avatar} sing${avatar === 1 ? "s" : ""} with Kling Avatar`,
      tone: "neutral",
    };
  if (!selected.length)
    return { label: "No singing shots selected", tone: "neutral" };
  const states = selected.map(vocalShotState);
  const audioChecks = states.filter((s: any) => s.key === "audio").length;
  if (audioChecks)
    return {
      label: `${audioChecks} audio check${audioChecks === 1 ? "" : "s"} needed`,
      tone: "attention",
    };
  const finished = states.filter((s: any) => s.key === "complete").length;
  if (finished === selected.length)
    return {
      label: `${finished}/${selected.length} lip-synced`,
      tone: "complete",
    };
  const toReview = states.filter((s: any) => s.key === "face").length;
  if (toReview)
    return {
      label: `${toReview} animated clip${toReview === 1 ? "" : "s"} to check`,
      tone: "attention",
    };
  const ready = states.filter((s: any) => s.key === "ready").length;
  if (ready) return { label: `${ready} ready for lip-sync`, tone: "ready" };
  if (selected.some((s: any) => s.lipsync_stale))
    return { label: "Lip-sync needs review", tone: "attention" };
  if (states.some((s: any) => s.key === "blocked"))
    return { label: "Lip-sync needs review", tone: "attention" };
  const skipped = states.filter((s: any) => s.key === "off").length;
  if (finished + skipped === selected.length)
    return {
      label: `${finished} lip-synced · ${skipped} skipped`,
      tone: "complete",
    };
  return { label: "Waiting for animation", tone: "neutral" };
}

export function vocalLabel(scene: any): string {
  const v = scene.vocal_direction;
  if (!v) return "";
  if (!v.valid) return "Lip-sync · selection needs review";
  if (!v.selected) return "No lip-sync · acting / dance";
  if (scene.lipsync_control?.mode === "off") return "No lip-sync · skipped";
  if (scene.singing_route === "avatar") return "Sings with Kling Avatar";
  if (!v.audio_confirmed) return "Singing selected · check audio";
  if (!scene.lipsync_source_asset_id || scene.motion_stale)
    return "Singing selected · animate first";
  if (scene.lipsync_ready && !scene.lipsync_stale) return "Lip-sync ready";
  return scene.lipsync_candidate
    ? "Lip-sync · ready to start"
    : "Singing selected · review animation";
}

export function vocalRequest(
  project: any,
  selected: string[],
  confirmed: string[],
): any {
  return {
    expected_storyboard_version: project.storyboard_version,
    expected_revision: project.vocal_direction.revision,
    singer_id: project.vocal_direction.singer_id,
    scene_ids: selected,
    audio_confirmed_scene_ids: confirmed.filter((id) => selected.includes(id)),
  };
}
